import React, { useCallback, useMemo } from "react";
import type { WrappedFieldMetaProps, WrappedFieldInputProps } from "redux-form";
import { Field } from "redux-form";
import type { InputType } from "design-system-old";
import { Input, NumberInput } from "design-system";
import styled from "styled-components";

import type { Intent } from "constants/DefaultTheme";
import { SettingSubtype } from "ee/pages/AdminSettings/config/types";
import { debounce, omit } from "lodash";

const StyledInput = styled(Input)`
  &&& input {
    &:hover {
      border-color: transparent !important;
      background:
        linear-gradient(white, white) padding-box,
        var(--ads-v2-color-bg-brand) border-box !important;
    }

    &:focus {
      border-color: transparent !important;
      background:
        linear-gradient(white, white) padding-box,
        var(--ads-v2-color-bg-brand) border-box !important;
      box-shadow: 0 0 0 2px rgba(19, 194, 194, 0.2) !important;
    }

    &:active {
      border-color: transparent !important;
      background:
        linear-gradient(white, white) padding-box,
        var(--ads-v2-color-bg-brand) border-box !important;
    }
  }
`;

const renderComponent = (
  componentProps: FormTextFieldProps & {
    meta: Partial<WrappedFieldMetaProps>;
    input: Partial<WrappedFieldInputProps>;
  },
) => {
  const showError = componentProps.meta.touched && !componentProps.meta.active;

  return componentProps.type === SettingSubtype.NUMBER ? (
    <NumberInput
      {...omit(componentProps, "type")}
      {...componentProps.input}
      errorMessage={
        !componentProps.hideErrorMessage && componentProps.meta.error
      }
      isDisabled={componentProps.disabled}
      label={componentProps.label as string}
    />
  ) : (
    <StyledInput
      {...componentProps.input}
      {...componentProps}
      errorMessage={
        !componentProps.hideErrorMessage &&
        showError &&
        componentProps.meta.error
      }
      isDisabled={componentProps.disabled}
      aria-label={componentProps.input.name}
      renderAs={"input"}
      size="md"
    />
  );
};

export interface FormTextFieldProps {
  name: string;
  placeholder: string;
  description?: string;
  type?: InputType;
  label?: React.ReactNode;
  intent?: Intent;
  disabled?: boolean;
  autoFocus?: boolean;
  hideErrorMessage?: boolean;
  isRequired?: boolean;
  startIcon?: string;
  className?: string;
}

function ReduxFormTextField(props: FormTextFieldProps) {
  return <Field component={renderComponent} {...props} asyncControl />;
}

export default ReduxFormTextField;
